/**
 * User and Credits API service
 * Handles all user-related and credit-related API calls using the Azure API system
 */

import { MIGRATION_CONFIG } from "../config/apiMigration.js";
import { FRONTEND_BASE_URL } from "../components/utils/apiConfig.js";

/**
 * Get the Azure API URL
 */
const getAPIUrl = () => MIGRATION_CONFIG.NEW_API_URL;

/**
 * Get feature mapping for job types
 */
const getFeatureMapping = () => MIGRATION_CONFIG.FEATURE_MAPPING;

/**
 * Get user credits information with detailed breakdown
 * @param {Object} user - Clerk user object
 * @returns {Promise<Object>} Credit information including detailed breakdown by feature
 * @returns {Promise<Object>} Object containing:
 *   - credits: Current available credits
 *   - planType: User's subscription plan type
 *   - planDaysLeft: Days remaining in current plan
 *   - totalCreditsUsed: Total credits consumed across all features
 *   - breakdown: Array of feature-specific credit usage details
 */
export const getUserCredits = async (user) => {
  if (!user) {
    throw new Error("User not signed in");
  }

  try {
    const response = await fetch(`${getAPIUrl()}/api/user/user-credits`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
      }),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Failed to fetch credit information" }));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const responseData = await response.json();
    console.log("User credits response:", responseData);

    if (responseData.success && responseData.data) {
      const creditData = responseData.data;
      const planType = (creditData.subscriptionType || "free").toLowerCase();
      // The Azure user-credits backend exposes subscriptionStartDate. Some
      // older response shapes also use planStartDate. Honor either.
      const planStartDate = creditData.subscriptionStartDate || creditData.planStartDate || null;
      // Compute the tech-pack sketch-edit cap on the client. Cap is purely a
      // function of (planType, subscriptionStartDate) — same helper that the
      // /api/tech-pack/edit-sketch endpoint uses server-side for defense in
      // depth, so client and server can never drift apart.
      let techPackSketchEditCap = 0;
      try {
        const { getTechPackSketchEditCap } = await import("../config/planRestrictions");
        techPackSketchEditCap = getTechPackSketchEditCap(planType, planStartDate);
      } catch (e) {
        console.warn("Could not compute techPackSketchEditCap:", e);
      }
      return {
        credits: creditData.currentCredits,
        planType,
        planDaysLeft: 30, // Default value, adjust as needed
        totalCreditsUsed: creditData.totalCreditsUsed,
        createdAt: creditData.createdAt || null,
        planStartDate,
        techPackSketchEditCap,
      };
    } else {
      throw new Error(
        responseData.error || "Failed to fetch credit information"
      );
    }
  } catch (error) {
    console.error("Error fetching user credits:", error);
    throw error;
  }
};

/**
 * Get user credit history with pagination
 * @param {Object} user - Clerk user object
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of records per page
 * @returns {Promise<Object>} Credit history with pagination info
 */
export const getCreditHistory = async (user, page = 0, limit = 10) => {
  if (!user) {
    throw new Error("User not signed in");
  }

  try {
    // Azure API system uses 1-based pagination
    const response = await fetch(`${getAPIUrl()}/api/user/credit-history`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
        page: page + 1, // Convert 0-based to 1-based
        limit: limit,
      }),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Failed to fetch credit history" }));
      throw new Error(errorData.error || "Failed to fetch credit history");
    }

    const data = await response.json();

    if (data.success && data.data) {
      return {
        transactions: data.data.transactions || [],
        pagination: {
          ...data.data.pagination,
          page: data.data.pagination.page - 1, // Convert back to 0-based
        },
      };
    } else {
      throw new Error(data.error || "Failed to fetch credit history");
    }
  } catch (error) {
    console.error("Error fetching credit history:", error);
    throw error;
  }
};

/**
 * Consume user credits for a specific feature
 * @param {number} amount - Number of credits to consume
 * @param {string} feature - Feature name (e.g., 'techpack', 'variation', 'moodboard')
 * @param {Object} user - Clerk user object (required for new API)
 * @returns {Promise<Object>} Response from credit consumption
 */
export const consumeCredits = async (amount, feature, user) => {
  if (!user) {
    throw new Error("User object required");
  }

  try {
    // Map feature to new system's job type
    const featureMapping = getFeatureMapping();
    const jobType = featureMapping[feature] || feature.toUpperCase();

    const response = await fetch(`${getAPIUrl()}/api/user/consume-credits`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
        amountConsumed: amount,
        jobType: jobType,
      }),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Failed to consume credits" }));

      // Check for insufficient credits error
      if (errorData.error && errorData.error.includes("Insufficient")) {
        throw new Error("INSUFFICIENT_CREDITS");
      }
      throw new Error(errorData.error || "Failed to consume credits");
    }

    const data = await response.json();

    if (data.success) {
      return {
        success: true,
        transactionId: data.data?.transactionId,
        message: "Credits consumed successfully",
      };
    } else {
      throw new Error(data.error || "Failed to consume credits");
    }
  } catch (error) {
    console.error("Error consuming credits:", error);
    throw error;
  }
};

/**
 * Get user plan information including plan type and days left
 * @param {Object} user - Clerk user object
 * @returns {Promise<Object>} User plan information including planType and daysLeft
 */
export const getUserPlanInfo = async (user) => {
  if (!user) {
    throw new Error("User not signed in");
  }

  try {
    // Reuse getUserCredits for consistency
    const creditData = await getUserCredits(user);
    return {
      planType: creditData.planType || "free",
      daysLeft: creditData.daysLeft || 30,
    };
  } catch (error) {
    console.error("Error fetching user plan info:", error);
    throw error;
  }
};

/**
 * Helper function to check which API system is currently active
 * @returns {boolean} True - always using new Azure API
 */
export const isUsingNewAPI = () => true;

/**
 * Helper function to get the current API base URL
 * @returns {string} Current API base URL
 */
export const getCurrentAPIUrl = () => getAPIUrl();

/**
 * Helper function to create a user in the new system (if needed)
 * @param {Object} user - Clerk user object
 * @returns {Promise<Object>} User creation response
 */
export const ensureUserExists = async (user) => {
  if (!user) {
    throw new Error("User object required");
  }

  try {
    // Try to verify user first
    const verifyResponse = await fetch(`${getAPIUrl()}/api/user/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
      }),
    });

    if (verifyResponse.ok) {
      return { exists: true };
    }

    // If user doesn't exist, create them
    const createResponse = await fetch(`${getAPIUrl()}/api/user/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
        emailId:
          user.emailAddresses?.[0]?.emailAddress || user.id + "@example.com",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        imageUrl: user.imageUrl || "",
      }),
    });

    if (!createResponse.ok) {
      const errorData = await createResponse
        .json()
        .catch(() => ({ error: "Failed to create user" }));
      throw new Error(errorData.error || "Failed to create user in system");
    }

    const data = await createResponse.json();
    return { exists: false, created: data.success };
  } catch (error) {
    console.error("Error ensuring user exists:", error);
    throw error;
  }
};

/**
 * Create a portal session for subscription management
 * @param {Object} user - Clerk user object
 * @returns {Promise<Object>} Portal session response with URL for redirection
 * @returns {Promise<Object>} Object containing:
 *   - success: boolean indicating if the operation was successful
 *   - url: string containing the portal session URL for redirection
 *   - sessionId: string containing the portal session ID
 */
export const createPortalSession = async (user) => {
  if (!user) {
    throw new Error("User not signed in");
  }

  try {
    const response = await fetch(
      `${getAPIUrl()}/api/payments/create-portal-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          emailId:
            user.primaryEmailAddress?.emailAddress ||
            user.emailAddresses?.[0]?.emailAddress,
          frontEndBaseUrl: FRONTEND_BASE_URL,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Failed to create portal session" }));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();

    if (data.success && data.data?.portalSessionUrl) {
      return {
        success: true,
        url: data.data.portalSessionUrl,
        sessionId: data.data.portalSessionId,
      };
    } else {
      throw new Error(
        data.error || "Failed to create portal session - no URL returned"
      );
    }
  } catch (error) {
    console.error("Error creating portal session:", error);
    throw error;
  }
};

/**
 * Verify a payment session and check user subscription eligibility
 * @param {Object} user - Clerk user object
 * @param {string} paymentSessionId - The payment session ID to verify
 * @returns {Promise<Object>} Payment verification response
 * @returns {Promise<Object>} Object containing:
 *   - success: boolean indicating if the operation was successful
 *   - isValid: boolean indicating if the payment is valid
 *   - subscriptionType: string containing the user's subscription type
 *   - subscriptionStatus: string containing the subscription status
 *   - subscriptionStartDate: Date when the subscription started
 *   - message: string with descriptive message about the verification result
 */
export const verifyPayment = async (user, paymentSessionId = "") => {
  if (!user) {
    throw new Error("User not signed in");
  }

  try {
    const response = await fetch(`${getAPIUrl()}/api/payments/verify-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
        paymentSessionId: paymentSessionId || "SamplePaymentSessionId",
      }),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Failed to verify payment" }));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();

    if (data.success && data.data) {
      return {
        success: true,
        isValid: data.data.isValid,
        subscriptionType: data.data.subscriptionType,
        subscriptionStatus: data.data.subscriptionStatus,
        subscriptionStartDate: data.data.subscriptionStartDate,
        message: data.data.message,
      };
    } else {
      throw new Error(
        data.error || "Failed to verify payment - no data returned"
      );
    }
  } catch (error) {
    console.error("Error verifying payment:", error);
    throw error;
  }
};

/**
 * Change user's subscription plan (upgrade or downgrade)
 * @param {Object} user - Clerk user object
 * @param {string} targetSubscriptionType - Target subscription type ('standard', 'pro', 'studio')
 * @param {string} changeType - When to apply the change ('immediate' or 'at_period_end')
 * @returns {Promise<Object>} Subscription change response
 * @returns {Promise<Object>} Object containing:
 *   - success: boolean indicating if the operation was successful
 *   - subscriptionId: string containing the updated subscription ID
 *   - currentPlan: string containing the current subscription plan
 *   - targetPlan: string containing the target subscription plan
 *   - changeEffectiveDate: Date when the change will take effect
 *   - prorationAmount: number containing the proration amount if applicable
 */
export const changeSubscription = async (
  user,
  targetSubscriptionType,
  changeType = "immediate"
) => {
  if (!user) {
    throw new Error("User not signed in");
  }

  if (!targetSubscriptionType) {
    throw new Error("Target subscription type is required");
  }

  // Validate subscription type
  const validSubscriptionTypes = [
    "standard", "pro", "studio",
    "standard_annual", "pro_annual", "studio_annual",
    "starter" // starter for legacy support
  ];
  if (!validSubscriptionTypes.includes(targetSubscriptionType.toLowerCase())) {
    throw new Error(
      "Invalid subscription type. Must be one of: standard, pro, studio, standard_annual, pro_annual, studio_annual"
    );
  }

  // Validate change type
  const validChangeTypes = ["immediate", "at_period_end"];
  if (!validChangeTypes.includes(changeType)) {
    throw new Error(
      "Invalid change type. Must be 'immediate' or 'at_period_end'"
    );
  }

  console.log({
    userId: user.id,
    targetSubscriptionType: targetSubscriptionType.toLowerCase(),
    changeType: changeType,
  });

  try {
    const response = await fetch(
      `${getAPIUrl()}/api/payments/change-subscription`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          targetSubscriptionType: targetSubscriptionType.toLowerCase(),
          changeType: changeType,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Failed to change subscription" }));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();

    if (data.success && data.data) {
      return {
        success: true,
        subscriptionId: data.data.subscriptionId,
        currentPlan: data.data.currentPlan,
        targetPlan: data.data.targetPlan,
        changeEffectiveDate: data.data.changeEffectiveDate,
        prorationAmount: data.data.prorationAmount,
      };
    } else {
      throw new Error(
        data.error || "Failed to change subscription - no data returned"
      );
    }
  } catch (error) {
    console.error("Error changing subscription:", error);
    throw error;
  }
};

/**
 * Create a checkout session for new subscription purchase
 * @param {Object} user - Clerk user object
 * @param {string} subscriptionType - Subscription type to purchase ('standard', 'pro', 'studio')
 * @param {Object} [billingAddress] - Optional billing address object
 * @param {string} [billingAddress.city] - City
 * @param {string} [billingAddress.country] - Country
 * @param {string} [billingAddress.state] - State
 * @param {string} [billingAddress.street] - Street address
 * @param {string} [billingAddress.zipcode] - ZIP/Postal code
 * @returns {Promise<Object>} Checkout session response
 * @returns {Promise<Object>} Object containing:
 *   - success: boolean indicating if the operation was successful
 *   - checkoutSessionId: string containing the checkout session ID
 *   - checkoutSessionUrl: string containing the checkout session URL for redirection
 */
export const createCheckoutSession = async (
  user,
  subscriptionType,
  billingAddress
) => {
  if (!user) {
    throw new Error("User not signed in");
  }

  if (!subscriptionType) {
    throw new Error("Subscription type is required");
  }

  // Validate subscription type
  const validSubscriptionTypes = [
    "standard", "pro", "studio",
    "standard_annual", "pro_annual", "studio_annual",
    "starter" // starter for legacy support
  ];
  if (!validSubscriptionTypes.includes(subscriptionType.toLowerCase())) {
    throw new Error(
      "Invalid subscription type. Must be one of: standard, pro, studio, standard_annual, pro_annual, studio_annual"
    );
  }

  // Validate billing address if provided
  if (billingAddress) {
    const requiredFields = ["city", "country", "state", "street", "zipcode"];
    const missingFields = requiredFields.filter(
      (field) => !billingAddress[field]
    );

    if (missingFields.length > 0) {
      throw new Error(
        `Billing address is incomplete. Missing fields: ${missingFields.join(
          ", "
        )}`
      );
    }
  }

  try {
    const requestBody = {
      userId: user.id,
      emailId:
        user.primaryEmailAddress?.emailAddress ||
        user.emailAddresses?.[0]?.emailAddress,
      subscriptionType: subscriptionType.toLowerCase(),
      frontEndBaseUrl: FRONTEND_BASE_URL,
    };

    // Add billing address if provided
    if (billingAddress) {
      requestBody.billingAddress = billingAddress;
    }

    const response = await fetch(
      `${getAPIUrl()}/api/payments/create-checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Failed to create checkout session" }));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();

    if (data.success && data.data) {
      return {
        success: true,
        checkoutSessionId: data.data.checkoutSessionId,
        checkoutSessionUrl: data.data.checkoutSessionUrl,
      };
    } else {
      throw new Error(
        data.error || "Failed to create checkout session - no data returned"
      );
    }
  } catch (error) {
    console.error("Error creating checkout session:", error);
    throw error;
  }
};
